import { useTranslation } from 'react-i18next'

const STATE_STYLE = {
  draft:     'bg-gray-100 text-gray-600',
  submitted: 'bg-amber-100 text-amber-700',
  approved:  'bg-blue-100 text-blue-700',
  sap:       'bg-violet-100 text-violet-700',
  rejected:  'bg-red-100 text-red-700',
  cancelled: 'bg-orange-100 text-orange-700',
  ordered:   'bg-indigo-100 text-indigo-700',
  received:  'bg-emerald-100 text-emerald-700',
  closed:    'bg-gray-200 text-gray-700',
}
const DOT_COLOR = {
  draft:     'bg-gray-400',
  submitted: 'bg-amber-400',
  approved:  'bg-blue-500',
  sap:       'bg-violet-500',
  ordered:   'bg-indigo-500',
  received:  'bg-emerald-500',
  closed:    'bg-gray-500',
}

const STEP_KEYS = ['draft', 'submitted', 'approved', 'sap', 'ordered', 'received', 'closed']

// Sub-status — only meaningful while the main state is "ordered".
const FULFILLMENT_STATUSES = ['fabrication', 'waiting_transport', 'transport', 'customs', 'expresit', 'arrived']
const FULFILLMENT_STYLE = {
  fabrication:        'bg-sky-100 text-sky-700',
  waiting_transport:  'bg-amber-100 text-amber-700',
  transport:          'bg-indigo-100 text-indigo-700',
  customs:            'bg-purple-100 text-purple-700',
  expresit:           'bg-cyan-100 text-cyan-700',
  arrived:            'bg-emerald-100 text-emerald-700',
}

// Payment — independent of both the state and the sub-status above.
const PAYMENT_STATUSES = ['pending', 'paid']
const PAYMENT_STYLE = {
  pending: 'bg-amber-100 text-amber-700',
  paid:    'bg-green-100 text-green-700',
}

export default function PurchaseFlowGuide() {
  const { t } = useTranslation(['purchases', 'common'])

  return (
    <div className="p-8 max-w-3xl">
      <div className="mb-8">
        <h1 className="text-[28px] font-bold text-gray-900 tracking-tight leading-tight">{t('flow.title')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('flow.subtitle')}</p>
      </div>

      {/* Diagram: a vertical step-by-step timeline — reads the same on desktop and phone */}
      <section className="mb-10">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">{t('flow.stepsTitle')}</h2>
        <ol className="relative border-l-2 border-gray-200 ml-2">
          {STEP_KEYS.map((key, idx) => {
            const trigger = idx > 0 ? t(`flow.steps.${key}.trigger`, { defaultValue: '' }) : ''
            return (
              <li key={key} className="ml-6 pb-8 last:pb-0">
                <span className={`absolute -left-[9px] mt-1 w-4 h-4 rounded-full ring-4 ring-white ${DOT_COLOR[key]}`} />
                {trigger && (
                  <div className="flex items-center gap-1 text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1.5">
                    <span aria-hidden="true">↓</span>
                    <span>{trigger}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATE_STYLE[key]}`}>{t(`states.${key}`)}</span>
                  <span className="text-xs text-gray-400">{t(`flow.steps.${key}.who`)}</span>
                </div>
                <p className="text-sm text-gray-600">{t(`flow.steps.${key}.desc`)}</p>
              </li>
            )
          })}
        </ol>

        <div className="mt-2 flex gap-2 px-4 py-3 bg-orange-50 border border-orange-100 rounded-lg text-sm text-orange-800">
          <span className="shrink-0 flex gap-1 h-fit">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATE_STYLE.rejected}`}>{t('states.rejected')}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATE_STYLE.cancelled}`}>{t('states.cancelled')}</span>
          </span>
          <p>{t('flow.branchNote')}</p>
        </div>
      </section>

      {/* Sub-status — only while "ordered", independent of the state above */}
      <section className="mb-10">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">{t('flow.tagsTitle')}</h2>
        <p className="text-sm text-gray-600 mb-4">{t('flow.tagsIntro')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FULFILLMENT_STATUSES.map(key => (
            <div key={key} className="bg-white border border-gray-200 rounded-lg px-3 py-2.5">
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mb-1 ${FULFILLMENT_STYLE[key]}`}>
                {t(`board.fulfillmentStatuses.${key}`)}
              </span>
              <p className="text-xs text-gray-500">{t(`flow.tags.${key}`)}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-3 italic">{t('flow.tagsExample')}</p>
      </section>

      {/* Payment status — independent of both the state and the sub-status */}
      <section className="mb-10">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">{t('flow.paymentTitle')}</h2>
        <p className="text-sm text-gray-600 mb-4">{t('flow.paymentIntro')}</p>
        <div className="flex gap-3">
          {PAYMENT_STATUSES.map(key => (
            <span key={key} className={`px-2 py-0.5 rounded-full text-xs font-medium ${PAYMENT_STYLE[key]}`}>
              {t(`board.paymentStatuses.${key}`)}
            </span>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-3 italic">{t('flow.paymentExample')}</p>
      </section>

      {/* Priority Queue */}
      <section>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">{t('flow.priorityTitle')}</h2>
        <p className="text-sm text-gray-600">{t('flow.priorityDesc')}</p>
      </section>
    </div>
  )
}
